<template>
  <section>
    <form method="post" @submit.prevent="onSend">
      <form-wrapper :validator="$v.form">
        <form-group horizontal name="name" :label="$t('attributes.name')">
          <b-input
            v-model="form.name"
            name="name"
            :placeholder="$t('message.please_input') + $t('attributes.name')"
            expanded
          >
          </b-input>
        </form-group>
        <form-group
          horizontal
          name="status"
          attribute="attributes.status"
          :label="$t('attributes.status')"
        >
          <b-switch v-model="form.status" name="status"></b-switch>
        </form-group>

        <form-group
          horizontal
          name="picture"
          attribute="attributes.picture"
          :label="$t('attributes.picture')"
          grouped
        >
          <template #label>
            {{ $t('attributes.picture') }}
            <b-tooltip type="is-dark" label="1920*640/限定jpg檔，600KB內">
              <b-icon
                size="is-small"
                type="is-danger"
                icon="help-circle-outline"
              ></b-icon>
            </b-tooltip>
          </template>
          <b-field class="file">
            <a class="button is-primary" @click="onSelectPictureImage">
              <b-icon icon="upload"></b-icon>
              <span>{{ $t('message.please_select_file') }}</span>
            </a>
            <p
              v-if="form.picture"
              class="tag is-warning"
              style="margin-left: 4px; height: 40px"
            >
              <a
                :href="CatalogBannerImage(form.picture)"
                @click.prevent="onClickImage(CatalogBannerImage(form.picture))"
              >
                {{ form.picture }}
              </a>
            </p>
          </b-field>
        </form-group>

        <form-group
          horizontal
          name="mobile_picture"
          attribute="attributes.mobile_picture"
          :label="$t('attributes.mobile_picture')"
          grouped
        >
          <template #label>
            {{ $t('attributes.mobile_picture') }}
            <b-tooltip type="is-dark" label="480*320/限定jpg檔，100KB內">
              <b-icon
                size="is-small"
                type="is-danger"
                icon="help-circle-outline"
              ></b-icon>
            </b-tooltip>
          </template>
          <b-field class="file">
            <a class="button is-primary" @click="onSelectMobilePictureImage">
              <b-icon icon="upload"></b-icon>
              <span>{{ $t('message.please_select_file') }}</span>
            </a>
            <p
              v-if="form.mobile_picture"
              class="tag is-warning"
              style="margin-left: 4px; height: 40px"
            >
              <a
                :href="CatalogBannerImage(form.mobile_picture)"
                @click.prevent="
                  onClickImage(CatalogBannerImage(form.mobile_picture))
                "
              >
                {{ form.mobile_picture }}
              </a>
            </p>
          </b-field>
        </form-group>

        <form-group
          horizontal
          name="picture_alt"
          attribute="attributes.image_alt"
          :label="$t('attributes.image_alt')"
        >
          <b-input
            v-model="form.picture_alt"
            name="picture_alt"
            :placeholder="
              $t('message.please_input') + $t('attributes.image_alt')
            "
            expanded
          >
          </b-input>
        </form-group>

        <form-group
          horizontal
          name="has_video"
          attribute="attributes.has_video"
          :label="$t('attributes.has_video')"
        >
          <b-switch v-model="form.has_video" name="has_video"></b-switch>
        </form-group>

        <form-group
          v-if="form.has_video"
          horizontal
          name="youtube_id"
          attribute="attributes.youtube_id"
          :label="$t('attributes.youtube_id')"
        >
          <b-field>
            <b-input
              v-model="form.youtube_id"
              name="youtube_id"
              :placeholder="
                $t('message.please_input') + $t('attributes.youtube_id')
              "
              expanded
              @input="onChangeId"
            >
            </b-input>
            <p class="control">
              <b-button
                class="button"
                @click.prevent="onClickImage(helpImageUrl('youtube_id.png'))"
              >
                說明
              </b-button>
            </p>
          </b-field>
        </form-group>

        <form-group
          v-if="!form.has_video"
          horizontal
          name="url"
          :label="$t('attributes.url')"
        >
          <b-input
            v-model="form.url"
            name="url"
            :disabled="form.has_video"
            :placeholder="$t('message.please_input') + $t('attributes.url')"
            expanded
          >
          </b-input>
        </form-group>
        <form-group
          horizontal
          name="effect_dates"
          attribute="attributes.effect_date"
          :label="$t('attributes.effect_date')"
        >
          <b-datepicker
            v-model="form.effect_dates"
            icon="calendar-today"
            trap-focus
            range
            :date-formatter="formatDates"
            :placeholder="
              $t('message.please_select') + $t('attributes.effect_date')
            "
            @input="onSelectEffectDates"
          >
          </b-datepicker>
        </form-group>
        <form-group
          horizontal
          name="selected_catalog"
          :label="$t('attributes.selected_catalogs')"
        >
          <div class="catalog-select-wrap-all mt-0">
            <div
              v-for="option in postCatalogs"
              :key="option.catalog_no"
              class="column is-3 is-marginless is-selectable catalog-select-tag"
            >
              <b-tag
                attached
                closable
                aria-close-label="Close tag"
                size="is-medium"
                @close="
                  onRemoveSelectedCatalogs(option, form.selected_catalogs)
                "
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
              </b-tag>
            </div>
          </div>
        </form-group>
        <form-group
          horizontal
          name="area_code"
          attribute="attributes.area"
          :label="$t('attributes.area')"
        >
          <b-select
            v-model="selectedArea"
            name="area_code"
            :loading="loadingAreas"
            :placeholder="$t('message.please_select') + $t('attributes.area')"
            expanded
            @input="onSelectArea"
          >
            <option value="" disabled>
              {{ $t('message.please_select') + $t('attributes.area') }}
            </option>
            <option v-for="option in areas" :key="option.code" :value="option">
              {{ option.name }}
            </option>
          </b-select>
        </form-group>

        <form-group horizontal name="catalog" :label="$t('attributes.catalog')">
          <div class="catalog-select-wrap">
            <div class="columns is-multiline is-mobile">
              <b-checkbox
                v-if="catalogsList.length > 0"
                v-model="showOffCatalogs"
                class="column is-4 is-marginless"
                @input="onShowOffCatalogs"
              >
                {{ $t('attributes.show_off_items') }}
              </b-checkbox>
            </div>
            <div class="columns is-multiline is-mobile">
              <b-checkbox
                v-for="option in catalogsList"
                v-model="form.selected_catalogs"
                :key="option.catalog_no"
                :native-value="option"
                :disabled="
                  form.selected_catalogs.findIndex(
                    c => c.catalog_no === option.catalog_no
                  ) !== -1
                "
                class="column is-3 is-marginless is-selectable"
                @input="selected => onSelectedCatalog(option, selected)"
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
                <a
                  @click.prevent.stop="onUpdateSort(option)"
                  style="font-size: 14px; color: blue;"
                >
                  {{ $t('attributes.sort') }}
                </a>
              </b-checkbox>
            </div>
          </div>
        </form-group>
        <b-field horizontal>
          <b-button
            :loading="sending"
            type="is-primary"
            native-type="submit"
            style="width: 100%; margin-top: 20px;"
          >
            {{ $t('attributes.update_data') }}
          </b-button>
        </b-field>
      </form-wrapper>
    </form>
    <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
    <b-modal
      :active.sync="isBannerSortModelActive"
      :can-cancel="false"
      full-screen
      has-modal-card
    >
      <catalog-banners-from
        :catalog-no="selectedCatalogNo"
        :catalog-name="selectedCatalogName"
      />
    </b-modal>
  </section>
</template>

<script>
import {
  required,
  maxLength,
  requiredIf,
  minLength
} from 'vuelidate/lib/validators'
import { OpenCatalogBannerFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'
import { AREA_API } from '@/utils/apis'
import CatalogBannersFrom from '@/components/catalogs/CatalogBannersFrom'

export default {
  name: 'BannerForm',
  components: { CatalogBannersFrom },
  props: {
    banner: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingAreas: false,
      areas: [],
      catalogs: [],
      catalogsList: [],
      selectedArea: null,
      selectedCatalog: null,

      sending: false,
      form: this.extractFormValues(this.banner),

      postCatalogs: this.banner.catalogs ? [...this.banner.catalogs] : [],
      showOffCatalogs: false,

      isImageModalActive: false,
      selectedImageUrl: null,

      selectedCatalogNo: '',
      selectedCatalogName: '',
      isBannerSortModelActive: false
    }
  },
  validations: {
    form: {
      id: { required },
      status: { required },
      name: { required, maxLength: maxLength(50) },
      picture: { required },
      mobile_picture: { required },
      has_video: { required },
      youtube_id: {
        requiredIf: requiredIf(function(model) {
          return model.has_video
        })
      },
      url: {
        requiredIf: requiredIf(function(model) {
          return model.has_video
        }),
        maxLength: maxLength(200)
      },
      picture_alt: {},
      effect_dates: {
        required,
        minLength: minLength(2)
      },
      catalog_banner_start: { required },
      catalog_banner_end: { required },
      selected_catalogs: { required }
    }
  },
  watch: {
    banner(newBanner) {
      this.id = newBanner.id
      this.postCatalogs = newBanner.catalogs ? [...newBanner.catalogs] : []
      this.form = this.extractFormValues(newBanner)
      this.loadAreas()
    }
  },
  computed: {
    formTitle() {
      let title = this.banner.id
        ? this.$t('attributes.modify')
        : this.$t('attributes.add')

      title += this.$t('attributes.banner')

      return title
    },
    isLoading() {
      return this.loadingAreas || this.sending
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.form.area_code) {
          this.selectedArea = this.areas.find(
            a => a.code === this.form.area_code
          )
          if (this.selectedArea) {
            this.catalogs = this.selectedArea.catalogs
            this.catalogsList = this.showOffCatalogs
              ? this.catalogs
              : this.catalogs.filter(c => c.status === true)
          } else {
            this.catalogs = []
            this.catalogsList = []
          }
        }
      } catch (e) {
        this.areas = []
        this.catalogs = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    onSelectArea(option) {
      this.selectedArea = option && option
      if (this.selectedArea) {
        this.catalogs = this.selectedArea.catalogs
        this.catalogsList = this.showOffCatalogs
          ? this.catalogs
          : this.catalogs.filter(c => c.status === true)
        this.selectedCatalogs = this.form.selected_catalogs
      } else {
        this.catalogs = []
        this.catalogsList = []
      }
    },
    onSelectCatalog(option) {
      this.selectedCatalog = option
      this.form.catalog_no = option && option.catalog_no
    },
    extractFormValues(data) {
      return {
        area_code: data.area_code,
        id: data.id,
        status: data.status,
        name: data.name,
        picture: data.picture,
        mobile_picture: data.mobile_picture,
        has_video: data.has_video,
        youtube_id: this.getVideoId(data),
        url: data.url,
        picture_alt: data.picture_alt,
        effect_dates:
          data.catalog_banner_start && data.catalog_banner_end
            ? [
                new Date(data.catalog_banner_start),
                new Date(data.catalog_banner_end)
              ]
            : [],

        catalog_banner_start:
          data.catalog_banner_start != null
            ? data.catalog_banner_start.split('T')[0]
            : data.catalog_banner_start,

        catalog_banner_end:
          data.catalog_banner_end != null
            ? data.catalog_banner_end.split('T')[0]
            : data.catalog_banner_end,

        selected_catalogs: data.catalogs
      }
    },
    getVideoId(banner) {
      if (!banner.has_video) {
        return null
      }

      const url = new URL(banner.url)
      const prefix = '/embed/'

      if (url.host !== 'www.youtube.com') {
        return null
      }

      if (!url.pathname.startsWith(prefix)) {
        return null
      }

      return url.pathname.slice(prefix.length)
    },
    onChangeId(id) {
      if (!id) {
        return
      }

      this.form.url = `https://www.youtube.com/embed/${id}?loop=1&playlist=${id}&showinfo=0&controls=0&modestbranding=0&fs=0&autoplay=1&rel=0&autohide=0`
    },
    async onSelectPictureImage() {
      try {
        this.form.picture = await OpenCatalogBannerFinder(null)
      } catch (e) {
        notifyService.error(e)
      }
    },
    async onSelectMobilePictureImage() {
      try {
        this.form.mobile_picture = await OpenCatalogBannerFinder(null)
      } catch (e) {
        notifyService.error(e)
      }
    },
    onShowOffCatalogs(selected) {
      this.showOffCatalogs = !!selected
      this.catalogsList = this.showOffCatalogs
        ? this.catalogs
        : this.catalogs.filter(c => c.status === true)
    },
    onSelectedCatalog(catalog, selectedCatalogs) {
      let index = selectedCatalogs.findIndex(
        c => c.catalog_no === catalog.catalog_no
      )
      const selected = index !== -1

      index = this.postCatalogs.findIndex(
        c => c.catalog_no === catalog.catalog_no
      )
      if (index === -1) {
        this.postCatalogs = [...this.postCatalogs, catalog]
      }

      index = this.form.selected_catalogs.findIndex(
        z => z.catalog_no === catalog.catalog_no
      )
      if (selected) {
        if (index === -1) {
          this.form.selected_catalogs = [
            ...this.form.selected_catalogs,
            catalog
          ]
        }
      } else {
        if (index !== -1) {
          this.form.selected_catalogs.splice(index, 1)
        }
      }
    },
    onRemoveSelectedCatalogs(catalog, selectedCatalogs) {
      let index = selectedCatalogs.findIndex(
        c => c.catalog_no === catalog.catalog_no
      )
      selectedCatalogs.splice(index, 1)
      this.form.selected_catalogs = selectedCatalogs
      this.postCatalogs = selectedCatalogs
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    onSelectEffectDates() {
      this.form.catalog_banner_start = this.formatDate(
        this.form.effect_dates[0]
      )
      this.form.catalog_banner_end = this.formatDate(this.form.effect_dates[1])
    },
    onUpdateSort(data) {
      this.selectedCatalogNo = data.catalog_no
      this.selectedCatalogName = data.name
      this.isBannerSortModelActive = true
    },
    async onSend() {
      if (this.form.has_video) {
        this.form.url = this.form.youtube_id
      }
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
